import type { Ref } from 'vue'

import { router } from '@inertiajs/vue3'
import { useCan } from '@twinepos/vue-rbac'
import { computed, readonly, ref } from 'vue'

import type { InventoryAdjustmentReasons } from '@/domains/adjustment-groups/types/consts/AdjustmentReason.consts'
import type {
  StockSaveRequest,
  StockSaveResult
} from '@/domains/command-center/composables/use-stock-mode/useStockAdjustmentSave.types'
import type {
  StockChosenReason,
  StockHeldScan,
  StockLocation,
  StockMenuEntry,
  StockMenuItem,
  StockModeStep,
  StockRow,
  StockSavedLine,
  StockVariant
} from '@/domains/command-center/handlers/stock/stockMode.types'
import type { EnterStockMode, MenuItem, SearchResultCommand } from '@/domains/command-center/types'
import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'
import type { AdjustmentMode } from '@/domains/quick-updates/types/models/QuickInventoryUpdate.types'

import { isBarcodeLookupPending, notifyBarcodeConsumed } from '@/composables/use-barcode-scanner/useBarcodeInjector'
import { useScannerEnterGuard } from '@/composables/use-barcode-scanner/useScannerEnterGuard'
import { REASON_DEFAULTS_BY_MODE } from '@/domains/adjustment-groups/types/consts/AdjustmentGroupMode.consts'
import { useStockAdjustmentSave } from '@/domains/command-center/composables/use-stock-mode/useStockAdjustmentSave'
import { useStockSearch } from '@/domains/command-center/composables/use-stock-mode/useStockSearch'
import { isSideTrip, useStockSteps } from '@/domains/command-center/composables/use-stock-mode/useStockSteps'
import {
  itemStepMenu,
  locationNeededMenu,
  locationStepMenu,
  notSavedLine,
  quantityStepMenu,
  reasonStepMenu,
  variantStepMenu
} from '@/domains/command-center/handlers/stock/stockMenu'
import {
  LOCALLY_FILTERED_STEPS,
  SAVING_HINTS,
  SIDE_TRIP_CRUMBS,
  STEP_HINTS,
  STEP_PLACEHOLDERS,
  STOCK_ERROR_ROW_ID,
  STOCK_MODE_NAME
} from '@/domains/command-center/handlers/stock/stockMode.consts'
import {
  inBaseUnits,
  incrementStockQuantity,
  parseStockQuantity,
  withoutScannedCode
} from '@/domains/command-center/handlers/stock/stockQuantity'
import { routeScan } from '@/domains/command-center/handlers/stock/stockScan'
import {
  shelfAt,
  stockUnitOfStockVariant,
  stockVariantName,
  stockVariantsCarrying,
  stockVariantsOf,
  toStockLocation,
  variantsMatchingScan
} from '@/domains/command-center/handlers/stock/stockVariants'
import { parseAndValidateBarcodes } from '@/domains/command-center/utils/barcodeUtils'
import { useLocationsQuery } from '@/domains/locations/composables/use-locations-query/useLocationsQuery'
import { useResolvedDefaultLocation } from '@/domains/locations/composables/use-resolved-default-location/useResolvedDefaultLocation'
import { useMeasurementUnitsQuery } from '@/domains/measurement-units/composables/use-measurement-units-query/useMeasurementUnitsQuery'
import { useToastStore } from '@/domains/notifications/store/use-toast-store/useToastStore'
import { useQuickUpdateConfig } from '@/domains/quick-updates/composables/use-quick-update-config/useQuickUpdateConfig'
import { formatStock } from '@/utils/unit-util/unitUtil'

/** The last code of a scan. Rapid scans arrive joined, as "A, B", and the command center has one reader of that. */
const latestCode = (scanned: string): string => parseAndValidateBarcodes(scanned).at(-1) ?? scanned.trim()

/**
 * The palette's stock mode: find or scan a variant, type a number, choose Add, Remove or Set.
 *
 * Composed from parts that each do one thing:
 * - `useStockSteps` — where stock mode is, and what the search box holds in each step;
 * - `routeScan` — what a scan means in the step it arrives in;
 * - the row builders in `handlers/stock/stockMenu.ts`;
 * - `useStockSearch` and `useStockAdjustmentSave` — the reads and the write.
 *
 * This composable holds the choices those parts read — the product, the variant, the location, the reason —
 * and reports each save from the request it sent.
 */
const useStockMode = (query: Ref<string | undefined>) => {
  const isActive = ref(false)
  const product = ref<IndexProduct | null>(null)
  const variantChoices = ref<StockVariant[]>([])
  const variant = ref<StockVariant | null>(null)
  const chosenLocationId = ref<number | null>(null)
  const chosenReason = ref<StockChosenReason | null>(null)
  /** The mode the reason step is choosing for, and whether choosing one saves at once. */
  const reasonMode = ref<AdjustmentMode>('add')
  const isSavingAfterReason = ref(false)
  const saved = ref<StockSavedLine | null>(null)
  const error = ref<string | null>(null)
  const held = ref<StockHeldScan | null>(null)
  /** The change sent and not yet answered, so the palette can show it on its way rather than look idle. */
  const pendingSave = ref<StockSaveRequest | null>(null)

  const { can } = useCan()
  const toastStore = useToastStore()
  // Held until stock mode opens. The palette mounts on every admin page, and these are its reads alone.
  const { data: locationsData, isLoading: isLoadingLocations } = useLocationsQuery({ enabled: isActive })
  const { defaultLocationId, isLoading: isLoadingDefaultLocation } = useQuickUpdateConfig({ enabled: isActive })
  const { resolvedLocationId } = useResolvedDefaultLocation(defaultLocationId, { enabled: isActive })
  // Held until stock mode opens, like the reads above: the palette mounts on every admin page.
  const { data: unitsData } = useMeasurementUnitsQuery({ enabled: isActive })
  const { mutate: saveAdjustment, isPending: isSaving } = useStockAdjustmentSave()
  const { isScannerEnter } = useScannerEnterGuard()

  const {
    step,
    searchText,
    quantityText,
    goTo,
    openSideTrip,
    closeSideTrip,
    reset: resetSteps
  } = useStockSteps({
    query,
    isActive,
    onQuantityEdited: () => {
      error.value = null
    }
  })

  // The search stands aside while a scan is looked up: both share one request token, and the later one would
  // abort the scan's lookup.
  const isSearchStep = computed(() => isActive.value && step.value === 'item' && !isBarcodeLookupPending.value)
  const { products, isSearching } = useStockSearch(searchText, isSearchStep)

  const locations = computed<StockLocation[]>(() => (locationsData.value ?? []).map(toStockLocation))

  /** The location is unknown until both the locations and the configured default have arrived. */
  const isResolvingLocation = computed(() => isLoadingLocations.value || isLoadingDefaultLocation.value)

  const location = computed<StockLocation | null>(() => {
    const id = chosenLocationId.value ?? resolvedLocationId.value

    return locations.value.find(candidate => candidate.id === id) ?? null
  })

  // A computation, not a gate. Starting a shelf and opening Movement History both sit behind
  // `inventory.view`, which the Stock category does not ask for, so it decides which rows are offered.
  // The routes enforce it anyway.
  const canViewInventory = computed(() => can('view', 'inventory'))

  // The typed number is in the stock unit; from here on it is the whole base count every row and save uses.
  const quantityInput = computed(() =>
    inBaseUnits(
      parseStockQuantity(quantityText.value),
      variant.value ? stockUnitOfStockVariant(variant.value, unitsData.value) : undefined
    )
  )

  /** A base count read in the variant's stock unit, as every stock figure in the palette reads. */
  const stockLabel = (baseCount: number, shown: StockVariant): string =>
    formatStock({ baseCount, unit: stockUnitOfStockVariant(shown, unitsData.value) })

  const reasonFor = (mode: AdjustmentMode): InventoryAdjustmentReasons | null =>
    chosenReason.value?.mode === mode ? chosenReason.value.reason : REASON_DEFAULTS_BY_MODE[mode]

  /** Still on the item the save was for. The merchant may have gone back, closed the palette, or moved on. */
  const isStillCounting = (request: StockSaveRequest): boolean =>
    isActive.value && variant.value?.id === request.variant.id

  /** The same item at the same location: the shelf the request writes to is the one on screen. */
  const isShelfOnScreen = (request: StockSaveRequest): boolean =>
    isStillCounting(request) && location.value?.id === request.location.id

  /** The save in flight, while its own shelf is on screen. A save for a shelf the merchant left shows nowhere. */
  const pendingSaveOnScreen = computed(() =>
    pendingSave.value && isShelfOnScreen(pendingSave.value) ? pendingSave.value : null
  )

  const quantityMenu = (): StockMenuEntry[] => {
    if (!variant.value) return []
    if (!location.value) return locationNeededMenu(stockVariantName(variant.value), isResolvingLocation.value)

    return quantityStepMenu({
      variant: variant.value,
      shelf: shelfAt(variant.value, location.value),
      location: location.value,
      input: quantityInput.value,
      reasonFor,
      error: error.value,
      held: held.value,
      canStartTracking: canViewInventory.value,
      saving: pendingSaveOnScreen.value,
      units: unitsData.value
    })
  }

  const variantMenu = (): StockMenuEntry[] => {
    if (!product.value) return []
    if (!location.value) return locationNeededMenu(product.value.title, isResolvingLocation.value)

    return variantStepMenu({
      product: product.value,
      variants: variantChoices.value,
      location: location.value,
      canStartTracking: canViewInventory.value
    })
  }

  const itemMenu = (): StockMenuEntry[] =>
    itemStepMenu({
      query: searchText.value.trim(),
      products: products.value,
      location: location.value,
      isLoadingLocations: isResolvingLocation.value,
      isSearching: isSearching.value,
      isLookingUp: isBarcodeLookupPending.value,
      saved: saved.value,
      canStartTracking: canViewInventory.value,
      canOpenHistory: canViewInventory.value
    })

  const menuByStep: Record<StockModeStep, () => StockMenuEntry[]> = {
    item: itemMenu,
    variant: variantMenu,
    quantity: quantityMenu,
    reason: () => reasonStepMenu(reasonMode.value, chosenReason.value?.reason ?? null),
    location: () => locationStepMenu(locations.value, location.value, isLoadingLocations.value)
  }

  const menuItems = computed<MenuItem[]>(() => menuByStep[step.value]())

  /** Every row, loose or grouped. */
  const rows = computed(() =>
    menuItems.value.flatMap(entry => (entry.items as StockMenuItem[] | undefined) ?? [entry as StockMenuItem])
  )

  /**
   * The step's status lines, such as the hint or a held scan, for a live region to say. The box never points
   * at a row that cannot be picked, so a screen reader would not reach them. A refused save is the alert's.
   */
  // The three live-region texts stay empty outside stock mode: the regions are always in the page, so that a
  // line present when stock mode opens is still announced as a change.
  const statusLine = computed(() =>
    isActive.value
      ? rows.value
          .filter(row => row.isStatus && row.id !== STOCK_ERROR_ROW_ID)
          .map(row => row.label)
          .join(' ')
      : ''
  )

  const saveError = computed(() => (isActive.value && error.value ? notSavedLine(error.value) : ''))

  /** The save as a screen reader hears it: on its way, then landed. A refused save is the alert's. */
  const saveStatus = computed(() => {
    if (!isActive.value) return ''
    if (pendingSave.value) return `Saving ${stockVariantName(pendingSave.value.variant)}…`
    if (!saved.value) return ''

    const { variant: savedVariant, location: savedAt, before, after } = saved.value

    return `Saved ${stockVariantName(savedVariant)} at ${savedAt.name}, from ${stockLabel(before, savedVariant)} to ${stockLabel(after, savedVariant)}`
  })

  const placeholder = computed(() => STEP_PLACEHOLDERS[step.value])

  const hints = computed(() =>
    step.value === 'quantity' && pendingSaveOnScreen.value ? SAVING_HINTS : STEP_HINTS[step.value]
  )

  const isFilteredLocally = computed(() => LOCALLY_FILTERED_STEPS.includes(step.value))

  const crumb = computed(() =>
    isSideTrip(step.value) ? `${STOCK_MODE_NAME} › ${SIDE_TRIP_CRUMBS[step.value]}` : STOCK_MODE_NAME
  )

  /** In the quantity step, or on a side trip from it. */
  const countingVariantId = computed(() =>
    step.value === 'item' || step.value === 'variant' ? null : (variant.value?.id ?? null)
  )

  const chooseVariant = (chosen: StockVariant, startingText = '') => {
    variant.value = chosen
    chosenReason.value = null
    error.value = null
    held.value = null
    goTo('quantity', startingText)
  }

  const chooseProduct = (chosen: IndexProduct, choices = stockVariantsOf(chosen)) => {
    if (choices.length === 1) {
      product.value = null
      chooseVariant(choices[0])

      return
    }

    product.value = chosen
    variantChoices.value = choices
    goTo('variant', '')
  }

  /** A variant chosen straight from the search belongs to no variant step, so Back returns to the search. */
  const chooseVariantRow = (chosen: StockVariant) => {
    if (step.value === 'item') product.value = null

    chooseVariant(chosen)
  }

  const reset = () => {
    resetSteps()
    product.value = null
    variantChoices.value = []
    variant.value = null
    chosenLocationId.value = null
    chosenReason.value = null
    isSavingAfterReason.value = false
    saved.value = null
    error.value = null
    held.value = null
    // `pendingSave` is kept: the request still runs, and only its answer clears it. `pendingSaveOnScreen` already
    // hides it until its shelf is on screen again.
  }

  const enter = (entry?: EnterStockMode) => {
    reset()
    isActive.value = true
    query.value = ''

    if (!entry?.product) return

    const choices = entry.code ? stockVariantsCarrying(entry.product, entry.code) : stockVariantsOf(entry.product)

    chooseProduct(entry.product, choices)
  }

  const exit = () => {
    reset()
    isActive.value = false
  }

  const openLocations = () => openSideTrip('location')

  const openReasons = (mode: AdjustmentMode, isThenSaving: boolean) => {
    reasonMode.value = mode
    isSavingAfterReason.value = isThenSaving
    openSideTrip('reason')
  }

  const openHistory = (shown: StockVariant, at: StockLocation) => {
    const params = new URLSearchParams({ 'variant[]': String(shown.id), 'location[]': String(at.id) })

    router.get(`${route('admin.inventory.adjustments.index')}?${params.toString()}`)
  }

  /**
   * ⚠ **Reported from the request that was sent, never from the screen.** A save answers after a moment, and
   * by then the palette may show another item or none.
   */
  const onSaved = (request: StockSaveRequest, { before, after }: StockSaveResult) => {
    pendingSave.value = null

    toastStore.add({
      state: 'once',
      severity: 'success',
      summary: 'Stock updated',
      detail: `${stockVariantName(request.variant)} at ${request.location.name}: ${stockLabel(before, request.variant)} → ${stockLabel(after, request.variant)}`
    })

    if (!isActive.value) return

    saved.value = { ...request, before, after }

    if (!isStillCounting(request)) return

    const next = held.value?.variant

    variant.value = null
    product.value = null

    if (next) {
      chooseVariant(next, '1')

      return
    }

    held.value = null
    goTo('item', '')
  }

  const onSaveFailed = (request: StockSaveRequest, failure: Error) => {
    pendingSave.value = null

    if (isStillCounting(request)) {
      error.value = failure.message
      goTo('quantity')

      return
    }

    toastStore.add({
      state: 'once',
      severity: 'error',
      summary: 'Stock not updated',
      detail: `${stockVariantName(request.variant)} at ${request.location.name}: ${failure.message}`
    })
  }

  const submit = (mode: AdjustmentMode, reason: InventoryAdjustmentReasons) => {
    const quantity = quantityInput.value.quantity
    if (!variant.value || !location.value || quantity === null || isSaving.value) return

    error.value = null

    const request: StockSaveRequest = { variant: variant.value, location: location.value, mode, quantity, reason }
    pendingSave.value = request
    // A new attempt retires the last one, so a failure is never followed by an older save's "Saved".
    saved.value = null

    saveAdjustment(request, {
      onSuccess: result => onSaved(request, result),
      onError: failure => onSaveFailed(request, failure)
    })
  }

  /** Remove has no default reason, so choosing it asks for one and then saves. */
  const saveOutcome = (mode: AdjustmentMode) => {
    const reason = reasonFor(mode)

    if (!reason) {
      openReasons(mode, true)

      return
    }

    submit(mode, reason)
  }

  const pickReason = (reason: InventoryAdjustmentReasons) => {
    chosenReason.value = { mode: reasonMode.value, reason }
    closeSideTrip()

    if (isSavingAfterReason.value) submit(reasonMode.value, reason)
  }

  const pickLocation = (picked: StockLocation) => {
    chosenLocationId.value = picked.id
    closeSideTrip()
  }

  const handleRow = (row: StockRow) => {
    switch (row.kind) {
      case 'product':
        return chooseProduct(row.product)
      case 'variant':
        return chooseVariantRow(row.variant)
      case 'outcome':
        return saveOutcome(row.mode)
      case 'changeReason':
        return openReasons(row.mode, false)
      case 'reason':
        return pickReason(row.reason)
      case 'location':
        return pickLocation(row.location)
      case 'openLocations':
        return openLocations()
      case 'saved':
        // The row is disabled for a user who may not open Movement History. The route refuses them anyway.
        return canViewInventory.value ? openHistory(row.saved.variant, row.saved.location) : undefined
      case 'saving':
        return undefined
    }
  }

  /**
   * A row was chosen, by click or by Enter on a highlighted row. True when it was taken; false for a row with
   * nothing behind it, or one a scanner's Enter picked.
   *
   * ⚠ **A scanner's Enter never chooses.** The box acts on Enter's keyup, after the scan has been taken and
   * its code removed from the box, so it would save whatever the merchant typed before the scan.
   * `useScannerEnterGuard` judged that Enter on its keydown.
   *
   * The saving row is taken although it does nothing, so the dialog still hands focus back to the box.
   */
  const select = (item: MenuItem): boolean => {
    const row = (item as StockMenuItem).stock
    if (!row || isScannerEnter()) return false

    handleRow(row)

    return true
  }

  /** Enter with nothing highlighted: the row a typed sign picked, if it can be saved. */
  const confirm = () => {
    if (step.value !== 'quantity' || isScannerEnter()) return

    const picked = quantityInput.value.mode
    const isSavable = rows.value.some(
      row => row.stock?.kind === 'outcome' && row.stock.mode === picked && !row.disabled
    )

    if (picked && isSavable) saveOutcome(picked)
  }

  const back = () => {
    if (step.value === 'item') return exit()
    if (step.value === 'variant') return goTo('item')
    if (isSideTrip(step.value)) return closeSideTrip()

    // Leaving an item unsaved: a held item, if any, opens in its place.
    const next = held.value?.variant

    variant.value = null
    held.value = null
    error.value = null

    if (next) {
      chooseVariant(next, '1')

      return
    }

    goTo(product.value ? 'variant' : 'item')
  }

  /**
   * A scan arrived while stock mode is open.
   *
   * It arrives twice: first as a preview while the lookup runs, then with the results. The preview only gives
   * the box back its own text, because the scanner typed the code into it.
   */
  const receiveScan = (scanned: string, results: SearchResultCommand[] | undefined) => {
    const code = latestCode(scanned)

    if (step.value === 'quantity') query.value = withoutScannedCode(query.value, code)
    if (!results) return

    // Spent, so the pipeline does not refuse the same code as "Already scanned" on the next count.
    notifyBarcodeConsumed()

    const route = routeScan({
      code,
      matches: variantsMatchingScan(results, code),
      countingVariantId: countingVariantId.value,
      hasQuantity: quantityInput.value.quantity !== null,
      isSaving: pendingSaveOnScreen.value !== null
    })

    if (route.kind === 'countAgain') return goTo('quantity', incrementStockQuantity(quantityText.value))

    if (route.kind === 'hold') {
      held.value = route.held

      return goTo('quantity')
    }

    if (route.kind === 'count') {
      product.value = null

      return chooseVariant(route.variant, '1')
    }

    goTo('item', route.code)
  }

  return {
    isActive: readonly(isActive),
    isSaving,
    menuItems,
    placeholder,
    hints,
    crumb,
    location,
    isFilteredLocally,
    statusLine,
    saveStatus,
    saveError,
    enter,
    exit,
    select,
    confirm,
    back,
    openLocations,
    receiveScan
  }
}

export { useStockMode }
